import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui-kit/dropdown-menu';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useOrganizationSwitcher } from '@/hooks/use-organization-switcher';
import { cn } from '@/lib/utils';

type OrganizationMenuSectionProps = {
  onOrganizationSelected?: () => void;
};

export const OrganizationMenuSection = ({
  onOrganizationSelected,
}: OrganizationMenuSectionProps) => {
  const { t } = useTranslation();
  const {
    availableOrganizations,
    handleOrganizationSelect,
    isLoading,
    isSwitching,
    selectedOrganization,
  } = useOrganizationSwitcher();

  if (isLoading) {
    return (
      <>
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-medium-emphasis">
          {t('SWITCH_ORGANIZATION')}
        </DropdownMenuLabel>
        <div className="px-2 pb-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="mt-2 h-8 w-full" />
        </div>
        <DropdownMenuSeparator />
      </>
    );
  }

  if (availableOrganizations.length === 0) {
    return null;
  }

  return (
    <>
      <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-medium-emphasis">
        {t('SWITCH_ORGANIZATION')}
      </DropdownMenuLabel>

      <div className="max-h-[220px] overflow-y-auto overscroll-contain">
        {availableOrganizations.map((org) => {
          const isSelected = org.itemId === selectedOrganization?.itemId;

          return (
            <DropdownMenuItem
              key={org.itemId}
              disabled={isSwitching}
              onClick={() => {
                void handleOrganizationSelect(org.itemId, onOrganizationSelected);
              }}
              className="flex cursor-pointer flex-col items-start gap-1 py-2.5"
            >
              <div className="flex w-full items-center gap-2">
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate font-medium',
                    isSelected ? 'text-primary' : 'text-high-emphasis'
                  )}
                >
                  {org.name}
                </span>
                <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                  {isSelected ? <Check className="h-4 w-4 text-primary" /> : null}
                </div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </div>

      <DropdownMenuSeparator />
    </>
  );
};
